import { useEffect, useMemo, useState } from 'react';

import {
  Popup,
  PopupAddBtn,
  PopupBody,
  PopupBreakRow,
  PopupCloseBtn,
  PopupDateValue,
  PopupDropdown,
  PopupDropdownItem,
  PopupDropdownScroll,
  PopupFields,
  PopupHeader,
  PopupHeaderTitle,
  PopupHelpText,
  PopupOverlay,
  PopupQtyInput,
  PopupRow,
  PopupRowLabel,
  PopupSelect,
  PopupSelectValue,
  PopupSelectWrap,
  TzLabel,
} from './styles';

const BREAK_OPTIONS_SHORT = ['0 min', '15 mins', '30 mins', '45 mins'];
const BREAK_OPTIONS_FULL = [
  '0 min',
  '15 mins',
  '30 mins',
  '45 mins',
  '60 mins',
];
const MIN_JOB_MINUTES = 4 * 60;
const BREAK_DISABLED_BELOW_MINUTES = 6 * 60;
const SIXTY_MIN_BREAK_AT_MINUTES = 8 * 60;
const TZ_LABEL = 'EDT';

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const pad2 = (n: number) => String(n).padStart(2, '0');

export const TIME_OPTIONS_15 = (() => {
  const out: string[] = [];
  for (let i = 0; i < 96; i++) {
    const total = i * 15;
    const h24 = Math.floor(total / 60);
    const mn = total % 60;
    const ap = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 % 12 || 12;
    out.push(`${h12}:${pad2(mn)} ${ap}`);
  }
  return out;
})();

export const time12To24 = (t: string) => {
  const m = t.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!m) return '00:00:00';
  let h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${pad2(h)}:${pad2(mn)}:00`;
};

export const time24To12 = (s: string) => {
  const m = s.match(/T?(\d+):(\d+)/);
  if (!m) return '6:00 AM';
  const h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 || 12;
  return `${h12}:${pad2(mn)} ${ap}`;
};

export const breakLabelFromMinutes = (mins: number) =>
  mins === 0 ? '0 min' : `${mins} mins`;

export const minutesFromBreakLabel = (label: string) =>
  parseInt(label, 10) || 0;

const labelToMinutes = (t: string) => {
  const m = t.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!m) return 0;
  let h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return h * 60 + mn;
};

const durationMinutes = (startLabel: string, endLabel: string) => {
  let diff = labelToMinutes(endLabel) - labelToMinutes(startLabel);
  if (diff <= 0) diff += 24 * 60;
  return diff;
};

const parseDateKey = (k: string) => {
  const [y, m, d] = k.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const centeredPopupPosition = () => {
  const pw = 400;
  const ph = 380;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    left: Math.round((vw - pw) / 2),
    top: Math.round(Math.max(40, (vh - ph) / 2)),
  };
};

export const popupPositionNearRect = (rect: DOMRect) => {
  const pw = 400;
  const ph = 380;
  const gap = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left =
    rect.right + gap + pw <= vw ? rect.right + gap : rect.left - gap - pw;
  if (left + pw > vw - 12) left = vw - pw - 12;
  if (left < 12) left = 12;
  let top = rect.top;
  if (top + ph > vh - 12) top = vh - ph - 12;
  if (top < 12) top = 12;
  return { left: Math.round(left), top: Math.round(top) };
};

const ChevronDown = ({ size = 24 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    style={{ width: size, height: size, flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ClockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    style={{ width: 24, height: 24, flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" style={{ width: 20, height: 20 }}>
    <path
      d="M15 5L5 15M5 5l10 10"
      stroke="#1C1C1C"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export type ShiftPopupValues = {
  startTime: string;
  endTime: string;
  breakTime: string;
  workers: number;
};

export type ShiftPopupMode = 'edit' | 'create';

type Props = {
  mode: ShiftPopupMode;
  orderTypeLabel: 'gig' | 'lta';
  dateKey: string;
  initial: ShiftPopupValues;
  position: { left: number; top: number };
  onSave: (values: ShiftPopupValues) => void;
  onClose: () => void;
  // When provided, the popup blocks saving a shift whose date+startTime is at
  // or before this moment. Mirrors the legacy "Shift time cannot be in the
  // past" guard, which only applies to client (customer) admins; tenant
  // admins are allowed to backdate within the PAST_DAYS window.
  pastCheck?: { todayKey: string; nowMinutes: number } | null;
};

const ShiftPopup = ({
  mode,
  orderTypeLabel,
  dateKey,
  initial,
  position,
  onSave,
  onClose,
  pastCheck,
}: Props) => {
  const [startTime, setStartTime] = useState(initial.startTime);
  const [endTime, setEndTime] = useState(initial.endTime);
  const [breakTime, setBreakTime] = useState(initial.breakTime);
  const [workers, setWorkers] = useState(initial.workers);
  const [openDropdown, setOpenDropdown] = useState<
    'start' | 'end' | 'break' | null
  >(null);

  const popupDurationMins = useMemo(
    () => durationMinutes(startTime, endTime),
    [startTime, endTime],
  );
  const breakDisabled = popupDurationMins < BREAK_DISABLED_BELOW_MINUTES;
  const breakOptions =
    popupDurationMins >= SIXTY_MIN_BREAK_AT_MINUTES
      ? BREAK_OPTIONS_FULL
      : BREAK_OPTIONS_SHORT;
  const durationTooShort = popupDurationMins < MIN_JOB_MINUTES;
  const startInPast = !!(
    pastCheck &&
    (dateKey < pastCheck.todayKey ||
      (dateKey === pastCheck.todayKey &&
        labelToMinutes(startTime) <= pastCheck.nowMinutes))
  );
  const disableSave = durationTooShort || startInPast;

  useEffect(() => {
    if (breakDisabled && breakTime !== '0 min') {
      setBreakTime('0 min');
    } else if (!breakDisabled && !breakOptions.includes(breakTime)) {
      setBreakTime(breakOptions[breakOptions.length - 1]);
    }
  }, [breakDisabled, breakOptions, breakTime]);

  const dt = parseDateKey(dateKey);
  const dateLabel = `${DAY_ABBR[dt.getDay()]}, ${MONTH_ABBR[dt.getMonth()]} ${dt.getDate()}`;

  const handleSubmit = () => {
    if (disableSave) return;
    onSave({ startTime, endTime, breakTime, workers });
  };

  return (
    <>
      <PopupOverlay dim={mode === 'edit'} onClick={onClose} />
      <Popup style={{ left: position.left, top: position.top }}>
        <PopupHeader>
          <PopupHeaderTitle>
            {mode === 'edit'
              ? 'Edit shift'
              : `Create ${orderTypeLabel === 'lta' ? 'LTA' : 'gig'} shift`}
          </PopupHeaderTitle>
          <PopupCloseBtn
            type="button"
            onClick={onClose}
            aria-label="Close popup"
          >
            <XIcon />
          </PopupCloseBtn>
        </PopupHeader>
        <PopupBody>
          <PopupFields>
            <PopupRow>
              <PopupRowLabel>Date</PopupRowLabel>
              <PopupDateValue>{dateLabel}</PopupDateValue>
            </PopupRow>

            <PopupRow>
              <PopupRowLabel>Time</PopupRowLabel>
              <PopupSelectWrap>
                <PopupSelect
                  onClick={() =>
                    setOpenDropdown(openDropdown === 'start' ? null : 'start')
                  }
                >
                  <PopupSelectValue placeholder={false}>
                    {startTime}
                  </PopupSelectValue>
                  <ClockIcon />
                </PopupSelect>
                {openDropdown === 'start' ? (
                  <PopupDropdown>
                    <PopupDropdownScroll>
                      {TIME_OPTIONS_15.map((t) => (
                        <PopupDropdownItem
                          key={t}
                          onClick={() => {
                            setStartTime(t);
                            const idx = TIME_OPTIONS_15.indexOf(t);
                            if (idx !== -1)
                              setEndTime(TIME_OPTIONS_15[(idx + 16) % 96]);
                            setOpenDropdown(null);
                          }}
                        >
                          {t}
                        </PopupDropdownItem>
                      ))}
                    </PopupDropdownScroll>
                  </PopupDropdown>
                ) : null}
              </PopupSelectWrap>
              <PopupSelectWrap>
                <PopupSelect
                  onClick={() =>
                    setOpenDropdown(openDropdown === 'end' ? null : 'end')
                  }
                >
                  <PopupSelectValue placeholder={false}>
                    {endTime}
                  </PopupSelectValue>
                  <ClockIcon />
                </PopupSelect>
                {openDropdown === 'end' ? (
                  <PopupDropdown>
                    <PopupDropdownScroll>
                      {TIME_OPTIONS_15.map((t) => (
                        <PopupDropdownItem
                          key={t}
                          onClick={() => {
                            setEndTime(t);
                            setOpenDropdown(null);
                          }}
                        >
                          {t}
                        </PopupDropdownItem>
                      ))}
                    </PopupDropdownScroll>
                  </PopupDropdown>
                ) : null}
              </PopupSelectWrap>
              <TzLabel>{TZ_LABEL}</TzLabel>
            </PopupRow>

            <PopupRow>
              <PopupRowLabel>Break</PopupRowLabel>
              <PopupBreakRow>
                <PopupSelectWrap>
                  <PopupSelect
                    disabled={breakDisabled}
                    onClick={() => {
                      if (breakDisabled) return;
                      setOpenDropdown(
                        openDropdown === 'break' ? null : 'break',
                      );
                    }}
                  >
                    <PopupSelectValue placeholder={false}>
                      {breakTime}
                    </PopupSelectValue>
                    <ChevronDown />
                  </PopupSelect>
                  {openDropdown === 'break' && !breakDisabled ? (
                    <PopupDropdown>
                      {breakOptions.map((opt) => (
                        <PopupDropdownItem
                          key={opt}
                          onClick={() => {
                            setBreakTime(opt);
                            setOpenDropdown(null);
                          }}
                        >
                          {opt}
                        </PopupDropdownItem>
                      ))}
                    </PopupDropdown>
                  ) : null}
                </PopupSelectWrap>
                {breakDisabled ? (
                  <PopupHelpText tone="info">
                    Shifts shorter than 6 hours may not have mandatory break
                    times.
                  </PopupHelpText>
                ) : null}
              </PopupBreakRow>
            </PopupRow>

            <PopupRow>
              <PopupRowLabel>Quantity</PopupRowLabel>
              <div style={{ flex: 1, minWidth: 0 }}>
                <PopupQtyInput
                  type="number"
                  min={1}
                  value={workers}
                  onChange={(e) =>
                    setWorkers(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                />
              </div>
            </PopupRow>
          </PopupFields>
          <PopupAddBtn
            type="button"
            onClick={handleSubmit}
            disabled={disableSave}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </PopupAddBtn>
          {durationTooShort ? (
            <PopupHelpText tone="error">
              Job duration should be at least 4 hours.
            </PopupHelpText>
          ) : startInPast ? (
            <PopupHelpText tone="error">
              Shift time cannot be in the past
            </PopupHelpText>
          ) : null}
        </PopupBody>
      </Popup>
    </>
  );
};

export default ShiftPopup;
